import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Card, CardContent, CardHeader } from '@/components/Card'
import { DetailsHeader } from '@/components/DetailsHeader'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  useMaterialPackContentSizes,
  useMaterialProductTypes,
} from '@/hooks/queries/crt/useMaterials'
import {
  TargetVolumesUpdate,
  useAvailableBasePeriods,
  useCreateTarget,
  useDeleteTarget,
  useUpdateTargetVolumes,
  useUpdateVolumeScenario,
  useVolumeScenarioById,
  volumeScenarioByIdQueryOptions,
} from '@/hooks/queries/crt/useVolumeScenarios'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { queryClient } from '@/main'
import { formatDateOnly, parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateVolumeScenario } from '@repo/backend/crt'
import {
  MonthKey,
  PRICE_SEGMENT_CODES,
  PRICE_SEGMENT_LABEL_KEYS,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABEL_KEYS,
  PRODUCT_CATEGORY_TO_ATTRIBUTE,
  ProductCategory,
  YearFlag,
} from '@repo/constants'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { toastClassName } from '@repo/ui/lib/constants'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PinnedByVersionsNote } from '../-components/PinnedByVersionsNote'
import { toast } from 'sonner'
import { grainKey } from './-components/TargetMonthlyVolumesEditor'
import { VolumeScenarioTargetCard } from './-components/VolumeScenarioTargetCard'

export const Route = createFileRoute('/_auth/_modules/crt/volume-scenarios/$volumeScenarioId')({
  component: VolumeScenarioDetails,
  loader: async ({ params: { volumeScenarioId } }) => {
    const scenario = await queryClient.ensureQueryData(
      volumeScenarioByIdQueryOptions(volumeScenarioId),
    )

    return {
      crumb: scenario.name,
    }
  },
})

type FormState = {
  name: string
  description: string
  baseCrtPeriod: Date | null
}

function VolumeScenarioDetails() {
  const { t } = useTranslation(['crt', 'materials'])
  const { canUser } = usePermissions()
  const { volumeScenarioId } = Route.useParams()

  const { data, isPending, isError } = useVolumeScenarioById(volumeScenarioId)
  const { data: basePeriods } = useAvailableBasePeriods()
  const { formatMonthYear } = useLocalizedDate()

  const canEdit = canUser('crt_volume_scenarios_edit')

  const [formState, setFormState] = useState<FormState>({
    name: '',
    description: '',
    baseCrtPeriod: null,
  })

  // targetId -> grainKey -> override value (null = revert to base)
  const [pendingEdits, setPendingEdits] = useState<Record<number, Record<string, number | null>>>(
    {},
  )

  const [category, setCategory] = useState<ProductCategory | ''>('')
  const [attributeValue, setAttributeValue] = useState('')

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
        baseCrtPeriod: data.baseCrtPeriod ? parseDateOnly(data.baseCrtPeriod) : null,
      })
    }
  }, [data])

  const { data: productTypes } = useMaterialProductTypes()
  const { data: packSizes } = useMaterialPackContentSizes(category || undefined)

  // Both silent so handleSave can coordinate the header + volumes save behind a single toast.
  const { mutateAsync: updateScenario, isPending: isUpdateScenarioPending } =
    useUpdateVolumeScenario({
      volumeScenarioId: Number(volumeScenarioId),
      showToast: false,
    })

  const { mutateAsync: updateTargetVolumes, isPending: isUpdateVolumesPending } =
    useUpdateTargetVolumes({
      volumeScenarioId: Number(volumeScenarioId),
      showToast: false,
    })

  const { mutate: createTarget, isPending: isCreateTargetPending } = useCreateTarget({
    volumeScenarioId: Number(volumeScenarioId),
    handleOnSuccess: () => {
      setCategory('')
      setAttributeValue('')
    },
  })

  const {
    mutate: deleteTarget,
    isPending: isDeleteTargetPending,
    variables: deletingTargetId,
  } = useDeleteTarget({ volumeScenarioId: Number(volumeScenarioId) })

  const getUnsavedHeaderChanges = (): UpdateVolumeScenario | null => {
    if (!data) return null

    const changes: UpdateVolumeScenario = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    const formBase = formState.baseCrtPeriod ? formatDateOnly(formState.baseCrtPeriod) : null
    if (formBase !== (data.baseCrtPeriod ?? null)) {
      changes.baseCrtPeriod = formBase
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasPendingVolumeEdits = Object.keys(pendingEdits).length > 0
  const hasUnsavedChanges = getUnsavedHeaderChanges() !== null || hasPendingVolumeEdits

  const attributeKey = category ? PRODUCT_CATEGORY_TO_ATTRIBUTE[category] : null

  const attributeOptions: { value: string; label: string }[] =
    attributeKey === 'productType'
      ? (productTypes ?? []).map((value) => ({ value, label: value }))
      : attributeKey === 'packContentSize'
        ? (packSizes ?? [])
            .filter((value) => value.trim() !== '' && !Number.isNaN(Number(value)))
            .sort((a, b) => Number(a) - Number(b))
            .map((value) => ({ value, label: value }))
        : attributeKey === 'priceSegment'
          ? PRICE_SEGMENT_CODES.map((code) => ({
              value: code,
              label: t(PRICE_SEGMENT_LABEL_KEYS[code], { ns: 'materials' }),
            }))
          : []

  const handleEditVolume = (
    targetId: number,
    yearFlag: YearFlag,
    month: MonthKey,
    value: number | null,
  ) => {
    const key = grainKey(yearFlag, month)
    const target = data?.targets.find((entry) => entry.id === targetId)
    const resolved = target?.volumes.find((v) => v.yearFlag === yearFlag && v.month === month)
    const savedOverride = resolved?.override ?? null

    setPendingEdits((prev) => {
      const targetEdits = { ...(prev[targetId] ?? {}) }

      if (value === savedOverride) {
        delete targetEdits[key]
      } else {
        targetEdits[key] = value
      }

      const next = { ...prev }
      if (Object.keys(targetEdits).length === 0) {
        delete next[targetId]
      } else {
        next[targetId] = targetEdits
      }
      return next
    })
  }

  const handleAddTarget = () => {
    if (!category || !attributeKey || !attributeValue) return
    createTarget({ productCategory: category, attributeKey, attributeValue })
  }

  const handleSave = async () => {
    const headerChanges = getUnsavedHeaderChanges()

    const volumeUpdates: TargetVolumesUpdate[] = Object.entries(pendingEdits).map(
      ([targetId, edits]) => ({
        targetId: Number(targetId),
        entries: Object.entries(edits).map(([key, amount]) => {
          const [yearFlag, month] = key.split('-') as [YearFlag, MonthKey]
          return { yearFlag, month, amount }
        }),
      }),
    )

    if (!headerChanges && volumeUpdates.length === 0) return

    try {
      await Promise.all([
        headerChanges ? updateScenario(headerChanges) : null,
        volumeUpdates.length > 0 ? updateTargetVolumes(volumeUpdates) : null,
      ])

      setPendingEdits({})

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    } catch (error) {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    }
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.volumeScenarios.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <PinnedByVersionsNote driver="volumeScenarios" entityId={volumeScenarioId} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={(name) => setFormState((prev) => ({ ...prev, name }))}
          onUpdateDescription={(description) => setFormState((prev) => ({ ...prev, description }))}
          permission="crt_volume_scenarios_edit"
        />

        <HStack gap="md" className="items-end">
          <VStack gap="xs" className="items-end">
            <Text tone="muted" size="xs">
              {t('volumeScenarios.baseForecast')}
            </Text>
            <Select
              value={formState.baseCrtPeriod ? formatDateOnly(formState.baseCrtPeriod) : undefined}
              onValueChange={(value) =>
                setFormState((prev) => ({ ...prev, baseCrtPeriod: parseDateOnly(value) }))
              }
              disabled={!canEdit}
            >
              <SelectTrigger className="bg-background w-48">
                <SelectValue placeholder={t('volumeScenarios.selectBaseForecast')} />
              </SelectTrigger>
              <SelectContent>
                {(basePeriods ?? []).map((period) => {
                  const value = formatDateOnly(period)
                  return (
                    <SelectItem key={value} value={value}>
                      {formatMonthYear(period)}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </VStack>
        </HStack>
      </HStack>

      <PermissionsGuard required={['crt_volume_scenarios_edit']}>
        <Card>
          <CardHeader>
            <Text weight="semibold">{t('volumeScenarios.addTarget')}</Text>
          </CardHeader>
          <CardContent>
            <HStack gap="sm" className="items-end">
              <VStack gap="xs">
                <Text tone="muted" size="xs">
                  {t('volumeScenarios.productCategory')}
                </Text>
                <Select
                  value={category || undefined}
                  onValueChange={(value) => {
                    setCategory(value as ProductCategory)
                    setAttributeValue('')
                  }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('volumeScenarios.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_CATEGORIES.map((productCategory) => (
                      <SelectItem key={productCategory} value={productCategory}>
                        {t(PRODUCT_CATEGORY_LABEL_KEYS[productCategory], { ns: 'materials' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </VStack>

              <VStack gap="xs">
                <Text tone="muted" size="xs">
                  {t('volumeScenarios.attribute')}
                </Text>
                <Select
                  value={attributeValue || undefined}
                  onValueChange={setAttributeValue}
                  disabled={!category}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('volumeScenarios.selectAttribute')} />
                  </SelectTrigger>
                  <SelectContent>
                    {attributeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </VStack>

              <Button
                type="button"
                onClick={handleAddTarget}
                disabled={!category || !attributeValue || isCreateTargetPending}
              >
                <PlusIcon className="size-4" />
                {t('volumeScenarios.addTarget')}
              </Button>
            </HStack>
          </CardContent>
        </Card>
      </PermissionsGuard>

      <VStack gap="lg">
        {data.targets.length === 0 ? (
          <Text tone="muted">{t('volumeScenarios.noTargets')}</Text>
        ) : (
          data.targets.map((target) => (
            <VolumeScenarioTargetCard
              key={target.id}
              volumeScenarioId={volumeScenarioId}
              target={target}
              pendingEdits={pendingEdits[target.id] ?? {}}
              canEdit={canEdit}
              isDeletePending={isDeleteTargetPending && deletingTargetId === target.id}
              onEditVolume={(yearFlag, month, value) =>
                handleEditVolume(target.id, yearFlag, month, value)
              }
              onDelete={() => deleteTarget(target.id)}
            />
          ))
        )}
      </VStack>

      <PermissionsGuard required={['crt_volume_scenarios_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdateScenarioPending || isUpdateVolumesPending}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
